import { Head } from '@inertiajs/react'
import { useState } from 'react'
import Logo from '@/components/ui/Logo'

interface LoginProps {
  logo_path?: string
  errors?: { email?: string }
  csrf_token?: string
}

function getCSRFToken(): string {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta?.getAttribute('content') || ''
}

export default function Login({ logo_path, errors: serverErrors, csrf_token }: LoginProps) {
  const errors = serverErrors || {}
  const token = csrf_token || getCSRFToken()

  // `submitting` flips on form submit so the inputs go readOnly and the
  // button disables with a spinner. Devise sign_in is a native POST → if
  // the server is slow (cold container, slow page load post-redirect), the
  // user otherwise sees no feedback and may click Sign in twice.
  //
  // Inputs use `readOnly` (not `disabled`) on purpose: disabled fields are
  // excluded from native form submission, so React's synchronous re-render
  // inside onSubmit would strip email/password from the POST body and
  // produce "Invalid email or password". `readOnly` keeps the values in
  // the form data while still preventing edits.
  //
  // If the server rejects credentials, Devise renders the page again with
  // an `errors.email` prop; the page re-mounts and submitting resets to
  // false naturally. No manual reset needed.
  const [submitting, setSubmitting] = useState(false)

  return (
    <>
      <Head title="Sign In" />
      <div className="relative isolate flex min-h-screen items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
        <div aria-hidden="true" className="absolute inset-0 -z-10 flex items-center justify-center overflow-hidden">
          <span
            className="select-none bg-gradient-to-tr from-[#ff80b5] to-[#9089fc] bg-clip-text text-[40rem] font-black leading-none text-transparent opacity-20 blur-3xl"
          >
            W
          </span>
        </div>
        <div className="w-full max-w-sm space-y-10">
          <div>
            {logo_path ? (
              <img
                alt="Warehouse"
                src={logo_path}
                className="mx-auto h-10 w-auto"
              />
            ) : (
              <Logo size="xl" variant="bare" className="mx-auto" />
            )}
            <h2 className="mt-10 text-center text-2xl/9 font-bold tracking-tight text-gray-900">
              Sign in to your account
            </h2>
          </div>

          {errors.email && (
            <div className="rounded-md bg-red-50 p-4" role="alert">
              <p className="text-sm text-red-700">{errors.email}</p>
            </div>
          )}

          <form
            action="/users/sign_in"
            method="POST"
            className="space-y-6"
            onSubmit={() => setSubmitting(true)}
          >
            <input type="hidden" name="authenticity_token" value={token} />

            <div>
              <div className="col-span-2">
                <input
                  id="email-address"
                  name="user[email]"
                  type="email"
                  required
                  placeholder="Email address"
                  autoComplete="email"
                  aria-label="Email address"
                  readOnly={submitting}
                  className="block w-full rounded-t-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:relative focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 read-only:cursor-not-allowed read-only:bg-gray-50 read-only:text-gray-500 sm:text-sm/6"
                />
              </div>
              <div className="-mt-px">
                <input
                  id="password"
                  name="user[password]"
                  type="password"
                  required
                  placeholder="Password"
                  autoComplete="current-password"
                  aria-label="Password"
                  readOnly={submitting}
                  className="block w-full rounded-b-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:relative focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 read-only:cursor-not-allowed read-only:bg-gray-50 read-only:text-gray-500 sm:text-sm/6"
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex gap-3">
                <div className="flex h-6 shrink-0 items-center">
                  <div className="group grid size-4 grid-cols-1">
                    <input
                      id="remember-me"
                      name="user[remember_me]"
                      type="checkbox"
                      value="1"
                      className="col-start-1 row-start-1 appearance-none rounded-sm border border-gray-300 bg-white checked:border-indigo-600 checked:bg-indigo-600 indeterminate:border-indigo-600 indeterminate:bg-indigo-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                    />
                    <svg
                      fill="none"
                      viewBox="0 0 14 14"
                      className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-disabled:stroke-gray-950/25"
                    >
                      <path
                        d="M3 8L6 11L11 3.5"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="opacity-0 group-has-checked:opacity-100"
                      />
                    </svg>
                  </div>
                </div>
                <label htmlFor="remember-me" className="block text-sm/6 text-gray-900">
                  Remember me
                </label>
              </div>

              <div className="text-sm/6">
                <a href="/users/password/new" className="font-semibold text-indigo-600 hover:text-indigo-500">
                  Forgot password?
                </a>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={submitting}
                className="flex w-full items-center justify-center gap-2 rounded-md bg-indigo-600 px-3 py-1.5 text-sm/6 font-semibold text-white shadow-xs hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:cursor-not-allowed disabled:bg-indigo-400"
              >
                {submitting && (
                  <svg className="-ml-1 h-4 w-4 animate-spin text-white" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" className="opacity-25" />
                    <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="3" strokeLinecap="round" className="opacity-90" />
                  </svg>
                )}
                {submitting ? 'Signing in…' : 'Sign in'}
              </button>
            </div>
          </form>
        </div>
      </div>
      <footer className="absolute bottom-4 left-0 w-full text-center text-sm text-gray-400">
        &copy; {new Date().getFullYear()}. Created at Ghost
      </footer>
    </>
  )
}
